// Deterministic per-foot ground-contact lifecycle for locomotion measurement.
//
// Contact truth comes ONLY from Box3D begin/end events (optional hit details).
// Foot height is never used. Drain order per fixed step: end → begin → hit.
// Same-step end+begin for a foot/ground pair finishes active (end then begin).

import type {
  BodyHandle,
  ContactBeginEvent,
  ContactEndEvent,
  ContactHitEvent,
} from 'box3d-web';

export type FootSide = 'left' | 'right';

/**
 * Bounded contact-trace ring (same 12 s @ 60 Hz policy as locomotion samples).
 * Oldest tokens drop first when the cap is exceeded.
 */
export const FOOT_CONTACT_TRACE_MAX_TOKENS = 720;

export interface FootContactTrackerOptions {
  /** Max retained contact-trace tokens (default {@link FOOT_CONTACT_TRACE_MAX_TOKENS}). */
  maxTraceTokens?: number;
}

export interface FootContactHandles {
  ground: BodyHandle;
  leftFoot: BodyHandle;
  rightFoot: BodyHandle;
}

export interface FootContactHitDetail {
  point: readonly [number, number, number];
  /** Canonical ground → foot normal (Y upward when feet plant on flat ground). */
  normal: readonly [number, number, number];
  approachSpeed: number;
}

export interface FootContactSideState {
  active: boolean;
  beginCount: number;
  endCount: number;
  consecutiveAirborneSteps: number;
  consecutivePlantSteps: number;
  lastHit: FootContactHitDetail | null;
  lastNormal: readonly [number, number, number] | null;
}

export interface FootContactTrackerSnapshot {
  generation: number;
  bound: boolean;
  left: FootContactSideState;
  right: FootContactSideState;
  invalidBodyEventCount: number;
  nonFootGroundContactCount: number;
  lastStepBeginCount: number;
  lastStepEndCount: number;
  lastStepHitCount: number;
  lastStepInvalidCount: number;
  lastStepNonFootGroundCount: number;
}

export interface FootContactStepIngest {
  ends: readonly ContactEndEvent[];
  begins: readonly ContactBeginEvent[];
  hits?: readonly ContactHitEvent[];
}

export interface FootContactTracker {
  readonly generation: number;
  /** Configured contact-trace token capacity (oldest dropped past this). */
  readonly maxTraceTokens: number;
  bind(handles: FootContactHandles): void;
  /** Clear active contact state and advance the contact-generation epoch. */
  resetAndAdvanceGeneration(): void;
  /**
   * Apply one fixed-step event batch. Call immediately after `world.step` and
   * before teardown / next game logic. Order: ends, begins, optional hits.
   */
  ingestStep(events: FootContactStepIngest): void;
  isContacting(side: FootSide): boolean;
  getSide(side: FootSide): FootContactSideState;
  snapshot(): FootContactTrackerSnapshot;
  getHandles(): FootContactHandles | null;
  /** Compact ordered contact-trace tokens for determinism hashing (bounded). */
  contactTraceTokens(): string[];
}

interface MutableSide {
  active: boolean;
  beginCount: number;
  endCount: number;
  consecutiveAirborneSteps: number;
  consecutivePlantSteps: number;
  lastHit: FootContactHitDetail | null;
  lastNormal: readonly [number, number, number] | null;
  /** Per-step edge flags for compact trace. */
  beganThisStep: boolean;
  endedThisStep: boolean;
  hitThisStep: boolean;
}

function emptySide(): MutableSide {
  return {
    active: false,
    beginCount: 0,
    endCount: 0,
    consecutiveAirborneSteps: 0,
    consecutivePlantSteps: 0,
    lastHit: null,
    lastNormal: null,
    beganThisStep: false,
    endedThisStep: false,
    hitThisStep: false,
  };
}

function cloneSide(side: MutableSide): FootContactSideState {
  return {
    active: side.active,
    beginCount: side.beginCount,
    endCount: side.endCount,
    consecutiveAirborneSteps: side.consecutiveAirborneSteps,
    consecutivePlantSteps: side.consecutivePlantSteps,
    lastHit: side.lastHit
      ? {
          point: [...side.lastHit.point] as [number, number, number],
          normal: [...side.lastHit.normal] as [number, number, number],
          approachSpeed: side.lastHit.approachSpeed,
        }
      : null,
    lastNormal: side.lastNormal
      ? ([...side.lastNormal] as [number, number, number])
      : null,
  };
}

function isValidBody(handle: BodyHandle | 0 | null | undefined): handle is BodyHandle {
  return typeof handle === 'number' && handle !== 0 && Number.isFinite(handle);
}

/**
 * Canonicalize native A→B normal to ground→foot (upward on flat ground).
 * Returns null when the pair is not a foot-ground hit.
 */
export function canonicalizeGroundToFootNormal(
  bodyA: BodyHandle,
  bodyB: BodyHandle,
  normal: Readonly<{ x: number; y: number; z: number }>,
  ground: BodyHandle,
  leftFoot: BodyHandle,
  rightFoot: BodyHandle,
): { side: FootSide; normal: readonly [number, number, number] } | null {
  const aGround = bodyA === ground;
  const bGround = bodyB === ground;
  if (!aGround && !bGround) return null;

  const other = aGround ? bodyB : bodyA;
  let side: FootSide | null = null;
  if (other === leftFoot) side = 'left';
  else if (other === rightFoot) side = 'right';
  if (!side) return null;

  // Native normal points bodyA → bodyB. We want ground → foot.
  if (aGround) {
    return { side, normal: [normal.x, normal.y, normal.z] };
  }
  return { side, normal: [-normal.x, -normal.y, -normal.z] };
}

export function createFootContactTracker(
  options: FootContactTrackerOptions = {},
): FootContactTracker {
  const maxTraceTokens = Math.max(
    1,
    (options.maxTraceTokens ?? FOOT_CONTACT_TRACE_MAX_TOKENS) | 0,
  );
  let generation = 0;
  let handles: FootContactHandles | null = null;
  let left = emptySide();
  let right = emptySide();
  let invalidBodyEventCount = 0;
  let nonFootGroundContactCount = 0;
  let lastStepBeginCount = 0;
  let lastStepEndCount = 0;
  let lastStepHitCount = 0;
  let lastStepInvalidCount = 0;
  let lastStepNonFootGroundCount = 0;
  const traceTokens: string[] = [];

  function getMutable(side: FootSide): MutableSide {
    return side === 'left' ? left : right;
  }

  function classifyPair(
    bodyA: BodyHandle,
    bodyB: BodyHandle,
  ):
    | { kind: 'invalid' }
    | { kind: 'unbound' }
    | { kind: 'footGround'; side: FootSide }
    | { kind: 'nonFootGround' }
    | { kind: 'other' } {
    if (!isValidBody(bodyA) || !isValidBody(bodyB)) return { kind: 'invalid' };
    if (!handles) return { kind: 'unbound' };

    const { ground, leftFoot, rightFoot } = handles;
    const aGround = bodyA === ground;
    const bGround = bodyB === ground;
    if (aGround || bGround) {
      const other = aGround ? bodyB : bodyA;
      if (other === leftFoot) return { kind: 'footGround', side: 'left' };
      if (other === rightFoot) return { kind: 'footGround', side: 'right' };
      // Ground touching something that is not a bound foot (self-body, prop, stale).
      return { kind: 'nonFootGround' };
    }

    // Foot-foot or other non-ground pairs are ignored for plant truth but not
    // counted as non-foot ground contacts.
    void leftFoot;
    void rightFoot;
    return { kind: 'other' };
  }

  function clearActiveState(): void {
    left = emptySide();
    right = emptySide();
    invalidBodyEventCount = 0;
    nonFootGroundContactCount = 0;
    lastStepBeginCount = 0;
    lastStepEndCount = 0;
    lastStepHitCount = 0;
    lastStepInvalidCount = 0;
    lastStepNonFootGroundCount = 0;
    traceTokens.length = 0;
  }

  return {
    get generation() {
      return generation;
    },

    get maxTraceTokens() {
      return maxTraceTokens;
    },

    bind(next: FootContactHandles): void {
      if (
        !isValidBody(next.ground) ||
        !isValidBody(next.leftFoot) ||
        !isValidBody(next.rightFoot)
      ) {
        // Reject stale/invalid bind: stay unbound and record diagnosis.
        handles = null;
        left = emptySide();
        right = emptySide();
        invalidBodyEventCount += 1;
        return;
      }
      handles = {
        ground: next.ground,
        leftFoot: next.leftFoot,
        rightFoot: next.rightFoot,
      };
      // Binding current handles must not inherit prior generation's active set.
      left = emptySide();
      right = emptySide();
      lastStepBeginCount = 0;
      lastStepEndCount = 0;
      lastStepHitCount = 0;
      lastStepInvalidCount = 0;
      lastStepNonFootGroundCount = 0;
    },

    resetAndAdvanceGeneration(): void {
      generation += 1;
      handles = null;
      clearActiveState();
    },

    ingestStep(events: FootContactStepIngest): void {
      left.beganThisStep = false;
      left.endedThisStep = false;
      left.hitThisStep = false;
      right.beganThisStep = false;
      right.endedThisStep = false;
      right.hitThisStep = false;

      let stepInvalid = 0;
      let stepNonFoot = 0;
      let stepBegin = 0;
      let stepEnd = 0;
      let stepHit = 0;

      // 1) Ends first.
      for (const ev of events.ends) {
        const cls = classifyPair(ev.bodyA, ev.bodyB);
        if (cls.kind === 'invalid' || cls.kind === 'unbound') {
          stepInvalid += 1;
          invalidBodyEventCount += 1;
          continue;
        }
        if (cls.kind === 'nonFootGround') {
          // Ends of non-foot ground contacts: still count for diagnostics once
          // they were counted on begin; do not double-count here.
          continue;
        }
        if (cls.kind !== 'footGround') continue;
        stepEnd += 1;
        const side = getMutable(cls.side);
        if (side.active) {
          side.active = false;
          side.endCount += 1;
          side.endedThisStep = true;
        } else {
          // Spurious end while already inactive — still record for diagnosis.
          side.endCount += 1;
          side.endedThisStep = true;
        }
      }

      // 2) Begins second (same-step end+begin finishes active).
      for (const ev of events.begins) {
        const cls = classifyPair(ev.bodyA, ev.bodyB);
        if (cls.kind === 'invalid' || cls.kind === 'unbound') {
          stepInvalid += 1;
          invalidBodyEventCount += 1;
          continue;
        }
        if (cls.kind === 'nonFootGround') {
          stepNonFoot += 1;
          nonFootGroundContactCount += 1;
          continue;
        }
        if (cls.kind !== 'footGround') continue;
        stepBegin += 1;
        const side = getMutable(cls.side);
        if (!side.active) {
          side.active = true;
          side.beginCount += 1;
          side.beganThisStep = true;
        } else {
          // Re-begin while already active: count begin, stay active.
          side.beginCount += 1;
          side.beganThisStep = true;
        }
      }

      // 3) Optional hits attach details only; never set/clear active alone.
      const hits = events.hits ?? [];
      if (handles) {
        for (const hit of hits) {
          stepHit += 1;
          if (!isValidBody(hit.bodyA) || !isValidBody(hit.bodyB)) {
            stepInvalid += 1;
            invalidBodyEventCount += 1;
            continue;
          }
          const canon = canonicalizeGroundToFootNormal(
            hit.bodyA,
            hit.bodyB,
            hit.normal,
            handles.ground,
            handles.leftFoot,
            handles.rightFoot,
          );
          if (!canon) {
            // Hit involving ground + non-foot is a diagnostic non-foot contact
            // if ground is in the pair; otherwise ignore.
            if (hit.bodyA === handles.ground || hit.bodyB === handles.ground) {
              // Already counted on begin when applicable; skip double-count.
            }
            continue;
          }
          const side = getMutable(canon.side);
          const detail: FootContactHitDetail = {
            point: [hit.point.x, hit.point.y, hit.point.z],
            normal: canon.normal,
            approachSpeed: hit.approachSpeed,
          };
          side.lastHit = detail;
          side.lastNormal = canon.normal;
          side.hitThisStep = true;
        }
      }

      // Consecutive plant / airborne counters (post-reconcile).
      for (const s of [left, right] as const) {
        if (s.active) {
          s.consecutivePlantSteps += 1;
          s.consecutiveAirborneSteps = 0;
        } else {
          s.consecutiveAirborneSteps += 1;
          s.consecutivePlantSteps = 0;
        }
      }

      lastStepBeginCount = stepBegin;
      lastStepEndCount = stepEnd;
      lastStepHitCount = stepHit;
      lastStepInvalidCount = stepInvalid;
      lastStepNonFootGroundCount = stepNonFoot;

      // Compact per-step token for determinism: gen|L:active,b,e,h|R:...|inv|nf
      const token = [
        `g${generation}`,
        `L${left.active ? 1 : 0}${left.beganThisStep ? 'B' : ''}${left.endedThisStep ? 'E' : ''}${left.hitThisStep ? 'H' : ''}`,
        `R${right.active ? 1 : 0}${right.beganThisStep ? 'B' : ''}${right.endedThisStep ? 'E' : ''}${right.hitThisStep ? 'H' : ''}`,
        `i${stepInvalid}`,
        `n${stepNonFoot}`,
      ].join('|');
      // Bound to maxTraceTokens (default 720 / 12s @ 60Hz); drop oldest first.
      if (traceTokens.length >= maxTraceTokens) {
        traceTokens.shift();
      }
      traceTokens.push(token);
    },

    isContacting(side: FootSide): boolean {
      return getMutable(side).active;
    },

    getSide(side: FootSide): FootContactSideState {
      return cloneSide(getMutable(side));
    },

    snapshot(): FootContactTrackerSnapshot {
      return {
        generation,
        bound: handles !== null,
        left: cloneSide(left),
        right: cloneSide(right),
        invalidBodyEventCount,
        nonFootGroundContactCount,
        lastStepBeginCount,
        lastStepEndCount,
        lastStepHitCount,
        lastStepInvalidCount,
        lastStepNonFootGroundCount,
      };
    },

    getHandles(): FootContactHandles | null {
      return handles
        ? {
            ground: handles.ground,
            leftFoot: handles.leftFoot,
            rightFoot: handles.rightFoot,
          }
        : null;
    },

    contactTraceTokens(): string[] {
      return traceTokens.slice();
    },
  };
}

/** FNV-1a 32-bit over contact-trace tokens (stable, allocation-light). */
export function hashContactTraceTokens(tokens: readonly string[]): string {
  let hash = 0x811c9dc5;
  for (const token of tokens) {
    for (let i = 0; i < token.length; i += 1) {
      hash ^= token.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    hash ^= 0x7c; // '|'
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}
